// Ещё открытые данные: «где самолёт» (ADS-B, adsb.lol), афиша (afisha.uz и каналы-афиши Telegram),
// туризм в Узбекистане в цифрах (Агентство статистики, SIAT).
import { useEffect, useState } from 'react';
import type { FlightTrack, LocalEvent, LocalEvents, TourismStats } from '@sayohat/shared';
import { addPoiToTrip, type ExploreTripItem } from '../../entities/poi';
import { api } from '../../shared/lib/api';
import { clientId } from '../../shared/lib/storage';
import { toast } from '../../shared/ui/Toast';
import { formatDay } from '../../shared/lib/format';
import { createStore, useStore } from '../../shared/lib/store';
import { Icon } from '../../shared/ui/Icon';

const fmt = (n: number) => Math.round(n).toLocaleString('ru-RU');
/** Источники с ключом — ссылка, когда позиция или расписание от них */
const KEYED: [string, string][] = [['SkyLink', 'https://skylinkapi.com'], ['AirLabs', 'https://airlabs.co'], ['AeroDataBox', 'https://aerodatabox.com'], ['Aviationstack', 'https://aviationstack.com']];

// ---------- Где самолёт ----------

/** Рейс, который смотрим (строка табло кладёт сюда номер) */
export const trackerStore = createStore({ flight: '', run: 0 });
export const trackFlight = (flight: string) => trackerStore.set((s) => ({ flight, run: s.run + 1 }));

/** «Следить в Telegram»: бот пришлёт регистрацию, выход, задержку, вылет и посадку */
function WatchLink({ flight }: { flight: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    api<{ url: string | null }>(`/api/flight-watch/link?flight=${encodeURIComponent(flight)}`).then((r) => { if (alive) setUrl(r.url); }, () => {});
    return () => { alive = false; };
  }, [flight]);
  const [busy, setBusy] = useState(false);
  // В браузере: разрешить уведомления (если ещё нет) и подписаться — статус придёт push-уведомлением
  const inBrowser = async () => {
    setBusy(true);
    try {
      if (typeof Notification !== 'undefined' && Notification.permission !== 'granted') {
        const { enablePush } = await import('../alerts/store');
        await enablePush();
      }
      const r = await api<{ flight: string; text: string }>('/api/flight-watch', { method: 'POST', body: { flight, client: clientId } });
      toast(`Следим за рейсом ${r.flight}. ${r.text}`);
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="ld-watch">
      {url ? <a className="ghost small-btn" href={url} target="_blank" rel="noopener"><Icon name="send" />Следить в Telegram</a> : null}
      <button type="button" className="ghost small-btn" disabled={busy} data-act="watch-browser" onClick={() => { void inBrowser(); }}><Icon name="bell" />Уведомлять в браузере</button>
    </span>
  );
}

export function FlightTracker() {
  const { flight, run } = useStore(trackerStore);
  const [input, setInput] = useState(flight);
  const [state, setState] = useState<{ data: FlightTrack | null; error: string; loading: boolean }>({ data: null, error: '', loading: false });
  useEffect(() => { setInput(flight); }, [flight]);
  // Ссылка со страницы рейса /f/HY601: /?track=HY601#flights — сразу ищем самолёт
  useEffect(() => {
    const q = new URLSearchParams(location.search).get('track');
    if (q && /^[A-Za-z0-9 -]{3,9}$/.test(q)) {
      trackFlight(q);
      setTimeout(() => document.getElementById('flight-tracker')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
    }
  }, []);
  useEffect(() => {
    if (!flight) return undefined;
    let alive = true;
    setState({ data: null, error: '', loading: true });
    api<FlightTrack>(`/api/local/track?flight=${encodeURIComponent(flight)}`).then(
      (data) => { if (alive) setState({ data, error: '', loading: false }); },
      (e: Error) => { if (alive) setState({ data: null, error: e.message, loading: false }); },
    );
    return () => { alive = false; };
  }, [flight, run]);
  const t = state.data;
  return (
    <div className="ld-track" id="flight-tracker">
      <form className="ld-track-form" onSubmit={(e) => { e.preventDefault(); if (input.trim()) trackFlight(input.trim()); }}>
        <b><Icon name="plane" /> Где самолёт</b>
        <input type="search" value={input} onChange={(e) => setInput(e.target.value)} placeholder="HY601 или UZB601" aria-label="Номер рейса" />
        <button type="submit" className="ghost small-btn"><Icon name="search" />Найти</button>
      </form>
      {state.loading ? <p className="muted small" role="status">Ищем самолёт…</p> : null}
      {state.error ? <p className="muted small">{state.error}</p> : null}
      {t ? <WatchLink flight={t.flight} /> : null}
      {t && !t.found ? <p className="muted small"><span data-no-i18n="">{t.callsign}</span> <span>сейчас не в воздухе или вне зоны приёма ADS-B.</span></p> : null}
      {t && t.found && t.position ? (
        <div className={t.estimated ? 'ld-track-card estimated' : 'ld-track-card'}>
          {t.photo ? (
            <a className="ld-track-photo" href={t.photo.link} target="_blank" rel="noopener">
              <img src={t.photo.url} alt="" loading="lazy" referrerPolicy="no-referrer" />
              <small data-no-i18n="">{`© ${t.photo.photographer} · planespotters.net`}</small>
            </a>
          ) : null}
          <div>
            <b data-no-i18n="">{t.flight}</b>
            {t.route?.from && t.route.to ? <span data-no-i18n="">{` ${t.route.from.iata || t.route.from.icao} → ${t.route.to.iata || t.route.to.icao}`}</span> : null}
            {t.route?.to?.city ? <span className="muted small" data-no-i18n="">{` (${t.route.to.city})`}</span> : null}
          </div>
          {t.estimated && t.estimate ? (
            <div className="ld-track-facts">
              <span className="pill warn">Расчётное положение</span>
              <span>{`пройдено ${Math.round(t.estimate.progress * 100)}%`}</span>
              <span>{`до посадки ≈ ${t.estimate.minutesLeft} мин`}</span>
              <span className="muted small">{t.estimate.basis === 'departure' ? 'По времени вылета с табло и маршруту — над этим районом приёмников ADS-B нет' : 'По времени прилёта с табло и маршруту — над этим районом приёмников ADS-B нет'}</span>
            </div>
          ) : (
            <div className="ld-track-facts">
              {t.position.onGround ? <span>На земле</span> : null}
              {t.position.altitudeM != null ? <span>{`Высота ${t.position.altitudeM} м`}</span> : null}
              {t.position.speedKmh != null ? <span>{`${t.position.speedKmh} км/ч`}</span> : null}
              {t.aircraft?.type ? <span data-no-i18n="">{[t.aircraft.type, t.aircraft.registration].filter(Boolean).join(' · ')}</span> : null}
              {t.owner ? <span className="muted" data-no-i18n="">{t.owner}</span> : null}
              <span className="muted">{`${t.position.secondsAgo} с назад`}</span>
            </div>
          )}
          {t.mapUrl ? <a className="ghost small-btn" href={t.mapUrl} target="_blank" rel="noopener"><Icon name="map" />На карте</a> : null}
        </div>
      ) : null}
      <p className="muted small">
        <span>Источники:</span> <a href="https://adsb.lol" target="_blank" rel="noopener" data-no-i18n="">adsb.lol</a> (ODbL), <a href="https://adsb.fi" target="_blank" rel="noopener" data-no-i18n="">adsb.fi</a>, <a href="https://avioadsb.org" target="_blank" rel="noopener" data-no-i18n="">avioadsb</a> (CC BY 4.0)
        {t?.source.startsWith('OpenSky') ? <>, <a href="https://opensky-network.org" target="_blank" rel="noopener" data-no-i18n="">OpenSky Network</a></> : null}
        {' · '}<span>маршрут —</span> <a href="https://adsb.im" target="_blank" rel="noopener" data-no-i18n="">adsb.im</a>, <a href="https://www.adsbdb.com" target="_blank" rel="noopener" data-no-i18n="">adsbdb</a>
        {KEYED.filter(([name]) => t?.source.includes(name)).map(([name, url]) => <span key={name}>, <a href={url} target="_blank" rel="noopener" data-no-i18n="">{name}</a></span>)}
        {' · '}<span>борт —</span> <a href="https://hexdb.io" target="_blank" rel="noopener" data-no-i18n="">hexdb.io</a>
      </p>
    </div>
  );
}

// ---------- Афиша ----------

/** Событие → позиция «Моего тура» с датой и временем ближайшего показа (2 часа) */
export function eventTrip(e: LocalEvent, city: string): ExploreTripItem {
  return {
    kind: 'activity', title: e.title, details: [e.place, e.source].filter(Boolean).join(' · '), price: null, currency: 'uzs', url: e.url,
    date: e.startsAt.slice(0, 10), time: /T\d{2}:\d{2}/.test(e.startsAt) ? e.startsAt.slice(11, 16) : undefined, durationMin: 120, city, image: e.image, refId: e.id,
  };
}

const eventsCache = new Map<string, Promise<LocalEvents>>();

export function EventsBlock({ city, cityName, id = 'local-events' }: { city: string; cityName?: string; id?: string }) {
  const [data, setData] = useState<LocalEvents | null>(null);
  useEffect(() => {
    let alive = true;
    setData(null);
    const key = city.toUpperCase();
    let p = eventsCache.get(key);
    if (!p) {
      p = api<LocalEvents>(`/api/local/events?city=${encodeURIComponent(key)}`).catch((e) => { eventsCache.delete(key); throw e; });
      eventsCache.set(key, p);
    }
    p.then((d) => { if (alive) setData(d); }, () => undefined);
    return () => { alive = false; };
  }, [city]);
  if (!data || (!data.events.length && !data.posts.length)) return null;
  return (
    <div className="ld-events" id={id}>
      {data.events.length ? (
        <ul className="ld-event-list">
          {data.events.slice(0, 8).map((e) => (
            <li key={e.id}>
              <a href={e.url} target="_blank" rel="noopener">
                {e.image ? <img src={e.image} alt="" loading="lazy" decoding="async" referrerPolicy="no-referrer" /> : <span className="ld-event-ph"><Icon name="ticket" /></span>}
                <span className="ld-event-body">
                  <b data-no-i18n="">{e.title}</b>
                  <small className="muted"><span>{formatDay(e.startsAt.slice(0, 10), { day: 'numeric', month: 'long' })}</span>{` · ${e.startsAt.slice(11, 16)}`}{e.dates.length > 1 ? <span>{` · +${e.dates.length - 1}`}</span> : null}</small>
                  {e.place ? <small className="muted" data-no-i18n="">{e.place}</small> : null}
                </span>
              </a>
              <button type="button" className="ghost ld-event-trip" data-trip={e.id} onClick={() => addPoiToTrip(eventTrip(e, cityName || ''))} aria-label="В мой тур" title="В мой тур"><Icon name="route" /></button>
            </li>
          ))}
        </ul>
      ) : null}
      {data.posts.length ? (
        <ul className="ld-post-list">
          {data.posts.slice(0, 4).map((p) => (
            <li key={p.url}>
              <a href={p.url} target="_blank" rel="noopener">
                <b data-no-i18n="">{p.channelTitle}</b> <span className="muted small">{formatDay(p.at.slice(0, 10), { day: 'numeric', month: 'short' })}</span>
                <span className="small" data-no-i18n="">{p.text}</span>
              </a>
            </li>
          ))}
        </ul>
      ) : null}
      <p className="muted small">
        <span>Афиша:</span>{' '}
        {data.sources.filter((s) => s.ok).map((s, i) => <span key={s.id}>{i > 0 ? ', ' : ''}<a href={s.url} target="_blank" rel="noopener" data-no-i18n="">{s.title}</a></span>)}
        {' · '}<span>подробности и билеты — на сайте организатора</span>
      </p>
    </div>
  );
}

// ---------- Туризм в цифрах ----------

let statsPromise: Promise<TourismStats> | null = null;

export function TourismStatsBlock({ id = 'tourism-stats' }: { id?: string }) {
  const [s, setS] = useState<TourismStats | null>(null);
  useEffect(() => {
    let alive = true;
    statsPromise ||= api<TourismStats>('/api/local/tourism-stats').catch((e) => { statsPromise = null; throw e; });
    statsPromise.then((d) => { if (alive) setS(d); }, () => undefined);
    return () => { alive = false; };
  }, []);
  if (!s || !s.inbound.series.length) return null;
  const last = (x: TourismStats['inbound']) => x.series[x.series.length - 1];
  const inb = last(s.inbound);
  const hotels = s.hotels.series.length ? last(s.hotels) : null;
  const firms = s.firms.series.length ? last(s.firms) : null;
  const max = Math.max(...s.inbound.series.map((p) => p.value));
  return (
    <div className="ld-stats" id={id}>
      <div className="ld-stats-nums">
        <span><b>{`${(inb.value / 1000).toFixed(1).replace('.', ',')} млн`}</b><small className="muted">{`поездок иностранцев в ${inb.year}`}{s.inbound.growth != null ? <span className={s.inbound.growth > 0 ? 'good' : ''}>{` · ${s.inbound.growth > 0 ? '+' : ''}${String(s.inbound.growth).replace('.', ',')}%`}</span> : null}</small></span>
        {hotels ? <span><b>{fmt(hotels.value)}</b><small className="muted">{`средств размещения в ${hotels.year}`}</small></span> : null}
        {firms ? <span><b>{fmt(firms.value)}</b><small className="muted">{`турфирм в ${firms.year}`}</small></span> : null}
      </div>
      <div className="ld-stats-bars" aria-hidden="true">
        {s.inbound.series.slice(-10).map((p) => <span key={p.year} style={{ height: `${Math.max(4, Math.round((p.value / max) * 100))}%` }} title={`${p.year}: ${fmt(p.value)}`}><i>{String(p.year).slice(2)}</i></span>)}
      </div>
      {s.countries.top.length ? (
        <p className="small"><b>Откуда едут</b>{` (${s.countries.year}): `}<span data-no-i18n="">{s.countries.top.slice(0, 6).map((c) => `${c.name} ${fmt(c.value)}`).join(', ')}</span></p>
      ) : null}
      <p className="muted small">Данные: <a href={s.sourceUrl} target="_blank" rel="noopener">Агентство статистики (SIAT)</a></p>
    </div>
  );
}
